<!-- Patient_CarePlanLegacy.vue -->
<template>
  <div class="h-full flex flex-col bg-gray-50">
    <!-- Loading -->
    <div v-if="loading" class="flex-1 flex flex-col overflow-hidden">
      <div class="bg-white border-b px-6 py-4">
        <div class="flex justify-between items-center">
          <div>
            <div class="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
            <div class="h-3 w-32 bg-gray-100 rounded animate-pulse mt-2"></div>
          </div>
          <div class="h-8 w-28 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>
      </div>
      <div class="flex-1 p-6 space-y-3">
        <div v-for="n in 3" :key="'sk-mod-' + n" class="bg-white rounded-lg border border-gray-200 p-4">
          <div class="flex items-center gap-2 mb-2">
            <div class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div class="h-4 bg-gray-200 rounded animate-pulse mb-2" :style="{ width: [200, 160, 180][n - 1] + 'px' }"></div>
          <div class="flex items-center gap-4 mt-2">
            <div class="h-4 w-16 bg-gray-100 rounded-full animate-pulse"></div>
            <div class="h-3 w-24 bg-gray-100 rounded animate-pulse"></div>
            <div class="h-3 w-20 bg-gray-100 rounded animate-pulse"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Care Plan -->
    <div v-else-if="!carePlan" class="flex-1 flex items-center justify-center">
      <div class="text-center">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">No Care Plan</h3>
        <button @click="createCarePlan" class="px-4 py-2 bg-indigo-600 text-white rounded-lg">
          Create Care Plan
        </button>
      </div>
    </div>

    <!-- Care Plan Content -->
    <div v-else class="flex-1 flex flex-col overflow-hidden">
      <!-- Header -->
      <div class="bg-white border-b px-6 py-4">
        <div class="flex justify-between items-center">
          <div>
            <h2 class="text-xl font-semibold">Care Plan</h2>
            <p class="text-sm text-gray-500 mt-1">
              Created: {{ formatDate(carePlan.created_at) }}
            </p>
          </div>
          <button @click="showAddModule = true" class="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm">
            <FontAwesomeIcon :icon="['fas', 'plus']" class="mr-2" />
            Add Module
          </button>
        </div>
      </div>

      <!-- Care Plan Modules -->
      <div class="flex-1 overflow-y-auto p-6 space-y-4">
        <!-- Empty state -->
        <div v-if="!carePlan.threads || carePlan.threads.length === 0" class="text-center py-12 text-gray-500">
          <FontAwesomeIcon :icon="['fas', 'clipboard-list']" class="text-4xl mb-3 opacity-50" />
          <p class="text-lg font-medium mb-2">No care plan modules yet</p>
          <p class="text-sm">Click "Add Module" to start building this patient's care plan</p>
        </div>

        <!-- Module cards -->
        <div v-else class="space-y-3">
          <div
            v-for="thread in carePlan.threads"
            :key="thread.id"
            class="bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
          >
            <!-- Module Header -->
            <div class="p-4">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <!-- Domain badge and title -->
                  <div class="flex items-center gap-2 mb-2">
                    <span
                      class="inline-flex items-center px-2 py-1 rounded text-xs font-medium"
                      :style="{
                        backgroundColor: getDomainColor(thread.thread_template, 0.1),
                        color: getDomainColor(thread.thread_template)
                      }"
                    >
                      <FontAwesomeIcon :icon="getDomainIcon(thread.thread_template)" class="mr-1" />
                      {{ thread.thread_template.name }}
                    </span>
                  </div>

                  <h4 class="font-medium text-gray-900 text-lg">
                    {{ thread.title || thread.metadata.title || 'Untitled Module' }}
                  </h4>

                  <div class="flex items-center space-x-4 mt-2">
                    <span
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getStatusClass(thread.metadata.status)"
                    >
                      {{ formatStatus(thread.metadata.status) }}
                    </span>
                    <span v-if="thread.metadata.template_used" class="text-xs text-gray-500">
                      Template: {{ getTemplateName(thread.thread_template, thread.metadata.template_used) }}
                    </span>
                    <span v-if="thread.metadata.typical_duration_weeks" class="text-xs text-gray-500">
                      Duration: {{ thread.metadata.typical_duration_weeks }} weeks
                    </span>
                    <span v-if="thread.subthreads && thread.subthreads.length > 0" class="text-xs text-gray-500">
                      <FontAwesomeIcon :icon="['fas', 'timeline']" class="mr-1" />
                      {{ thread.subthreads.length }} {{ thread.subthreads.length === 1 ? 'interaction' : 'interactions' }}
                    </span>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <button
                    @click="viewModule(thread)"
                    class="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    title="View Details"
                  >
                    <FontAwesomeIcon :icon="['fas', 'eye']" />
                  </button>
                  <button
                    @click="editModule(thread)"
                    class="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                    title="Edit Module"
                  >
                    <FontAwesomeIcon :icon="['fas', 'pen-to-square']" />
                  </button>
                  <button
                    @click="deleteModule(thread)"
                    class="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Module"
                  >
                    <FontAwesomeIcon :icon="['fas', 'trash']" />
                  </button>
                </div>
              </div>

              <!-- Domain-specific field values preview -->
              <div v-if="hasFieldValues(thread)" class="mt-3 pt-3 border-t">
                <div class="grid grid-cols-2 gap-2 text-sm">
                  <div v-for="(value, key) in getDisplayFieldValues(thread)" :key="key">
                    <span class="text-gray-500">{{ getFieldLabel(thread.thread_template, key) }}:</span>
                    <span class="ml-1 text-gray-900">{{ formatFieldValue(value) }}</span>
                  </div>
                </div>
              </div>

              <!-- Goals Summary -->
              <div v-if="thread.metadata.goals?.length" class="mt-3">
                <p class="text-xs font-medium text-gray-600 mb-2">Goals ({{ thread.metadata.goals.length }})</p>
                <div class="space-y-1">
                  <div
                    v-for="(goal, index) in thread.metadata.goals.slice(0, 2)"
                    :key="goal.id || index"
                    class="flex items-center text-sm"
                  >
                    <span
                      class="w-2 h-2 rounded-full mr-2"
                      :class="getGoalStatusClass(goal.status)"
                    ></span>
                    <span class="text-gray-700 truncate">{{ goal.title }}</span>
                  </div>
                  <div v-if="thread.metadata.goals.length > 2" class="text-xs text-gray-500 italic">
                    +{{ thread.metadata.goals.length - 2 }} more goals
                  </div>
                </div>
              </div>

              <!-- Interventions Summary -->
              <div v-if="thread.metadata.interventions?.length" class="mt-3">
                <p class="text-xs font-medium text-gray-600 mb-2">
                  Interventions ({{ thread.metadata.interventions.length }})
                </p>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="(intervention, index) in thread.metadata.interventions.slice(0, 3)"
                    :key="intervention.id || index"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700"
                  >
                    {{ intervention.title }}
                  </span>
                  <span
                    v-if="thread.metadata.interventions.length > 3"
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-500 italic"
                  >
                    +{{ thread.metadata.interventions.length - 3 }} more
                  </span>
                </div>
              </div>

              <!-- Subthreads Timeline Section -->
              <div v-if="thread.subthreads && thread.subthreads.length > 0" class="mt-3 pt-3 border-t">
                <div class="flex items-center justify-between mb-3">
                  <p class="text-xs font-medium text-gray-600">
                    <FontAwesomeIcon :icon="['fas', 'timeline']" class="mr-1" />
                    Patient Interactions ({{ thread.subthreads.length }})
                  </p>
                </div>
                <SubthreadTimeline
                  :subthreads="thread.subthreads"
                  mode="compact"
                  :max-compact-items="3"
                  :show-delete="true"
                  @delete="deleteSubthread($event, thread)"
                  @show-all="viewModuleWithSubthreads(thread)"
                />
              </div>

              <!-- Last Engagement -->
              <div v-if="thread.metadata.last_touchpoint" class="mt-3 pt-3 border-t text-xs text-gray-500">
                Last engagement: {{ formatDate(thread.metadata.last_touchpoint) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Module Modal -->
    <AddModuleModal
      v-if="showAddModule"
      :care-plan-id="carePlan?.id"
      :thread-types="threadTypes"
      @close="showAddModule = false"
      @added="onModuleAdded"
    />

    <!-- Edit Module Modal -->
    <EditModuleModal
      v-if="selectedModule"
      :module="selectedModule"
      :thread-type="selectedThreadType"
      @close="closeEditModal"
      @updated="loadCarePlan"
    />

    <!-- View Module Modal -->
    <ViewModuleModal
      v-if="viewingModule"
      :module="viewingModule"
      :thread-type="viewingThreadType"
      @close="closeViewModal"
      @edit-module="handleEditFromView"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import AddModuleModal from '@/components/Care_Management/Widgets/Care_Plan/CarePlan_AddModule_Modal.vue';
import EditModuleModal from '@/components/Care_Management/Widgets/Care_Plan/CarePlan_EditModule_Modal.vue';
import ViewModuleModal from '@/components/Care_Management/Widgets/Care_Plan/CarePlan_ViewModule_Modal.vue';
import SubthreadTimeline from '@/components/Care_Management/Widgets/Care_Plan/SubthreadTimeline.vue';

export default {
  name: 'PatientCarePlanLegacy',
  components: {
    FontAwesomeIcon,
    AddModuleModal,
    EditModuleModal,
    ViewModuleModal,
    SubthreadTimeline,
  },

  props: {
    patientIdentifier: {
      type: String,
      required: true,
    },
    entityId: {
      type: Number,
      required: true,
    },
  },

  emits: ['show-message'],

  data() {
    return {
      loading: false,
      carePlan: null,
      threadTypes: [],
      showAddModule: false,
      selectedModule: null,
      selectedThreadType: null,
      viewingModule: null,
      viewingThreadType: null,
    };
  },

  mounted() {
    this.loadCarePlan();
    this.loadThreadTypes();
  },

  watch: {
    entityId: {
      handler(newEntityId, oldEntityId) {
        if (newEntityId && newEntityId !== oldEntityId) {
          this.loadCarePlan();
        }
      },
    },
  },

  methods: {
    async loadThreadTypes() {
      try {
        const response = await this.$axios.get('api/careplans/thread-types/');
        this.threadTypes = response.data;
      } catch (error) {
        console.error('Error loading thread types:', error);
      }
    },

    async loadCarePlan() {
      this.loading = true;
      try {
        const response = await this.$axios.get('api/careplans/by-entity/', {
          params: {
            entity_id: this.entityId,
          },
        });

        if (response.data.success) {
          this.carePlan = response.data.care_plan;
        }
      } catch (error) {
        console.error('Error loading care plan:', error);
        this.carePlan = null;
      } finally {
        this.loading = false;
      }
    },

    async createCarePlan() {
      this.loading = true;
      try {
        const response = await this.$axios.post('api/careplans/', {
          entity_id: this.entityId,
        });

        if (response.data.success) {
          await this.loadCarePlan();
          this.$emit('show-message', 'Care plan created successfully', 'success');
        }
      } catch (error) {
        console.error('Error creating care plan:', error);
        this.$emit('show-message', 'Failed to create care plan', 'error');
      } finally {
        this.loading = false;
      }
    },

    getDomainColor(threadTemplate, opacity = 1) {
      const color =
        threadTemplate.metadata?.domain_config?.domain_color || '#6366F1';
      if (opacity === 1) return color;

      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    },

    getDomainIcon(threadTemplate) {
      const icon = threadTemplate.metadata?.domain_config?.domain_icon;
      if (!icon) return ['fas', 'layer-group'];

      if (typeof icon === 'string') {
        return ['fas', icon];
      }
      return icon;
    },

    getStatusClass(status) {
      const statusClasses = {
        active: 'bg-green-100 text-green-800',
        planned: 'bg-blue-100 text-blue-800',
        pending: 'bg-yellow-100 text-yellow-800',
        on_hold: 'bg-orange-100 text-orange-800',
        completed: 'bg-gray-100 text-gray-800',
        discontinued: 'bg-red-100 text-red-800',
      };
      return statusClasses[status] || 'bg-gray-100 text-gray-800';
    },

    getGoalStatusClass(status) {
      const statusClasses = {
        completed: 'bg-green-500',
        in_progress: 'bg-yellow-500',
        not_started: 'bg-gray-400',
        blocked: 'bg-red-500',
      };
      return statusClasses[status] || 'bg-gray-400';
    },

    formatStatus(status) {
      if (!status) return 'Unknown';
      return (
        status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ')
      );
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString();
    },

    getTemplateName(threadType, templateId) {
      const templates = threadType.metadata?.module_templates || [];
      const template = templates.find((t) => t.template_id === templateId);
      return template?.name || templateId;
    },

    hasFieldValues(thread) {
      return (
        thread.metadata?.field_values &&
        Object.keys(thread.metadata.field_values).length > 0
      );
    },

    getDisplayFieldValues(thread) {
      const fieldValues = thread.metadata?.field_values || {};
      const entries = Object.entries(fieldValues).slice(0, 3);
      return Object.fromEntries(entries);
    },

    getFieldLabel(threadType, fieldName) {
      const requiredFields = threadType.metadata?.fields?.required || {};
      const optionalFields = threadType.metadata?.fields?.optional || {};

      const field = requiredFields[fieldName] || optionalFields[fieldName];
      return field?.label || fieldName;
    },

    formatFieldValue(value) {
      if (value === null || value === undefined) return 'N/A';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'object') return JSON.stringify(value);
      return value.toString();
    },

    viewModule(module) {
      this.viewingModule = module;
      this.viewingThreadType = module.thread_template;
    },

    viewModuleWithSubthreads(module) {
      this.viewingModule = module;
      this.viewingThreadType = module.thread_template;
    },

    editModule(module) {
      this.selectedModule = module;
      this.selectedThreadType = module.thread_template;
    },

    async deleteModule(module) {
      if (!confirm('Are you sure you want to delete this module?')) {
        return;
      }

      try {
        await this.$axios.delete(`api/careplanthreads/${module.id}/`);
        this.$emit('show-message', 'Module deleted successfully', 'success');
        this.loadCarePlan();
      } catch (error) {
        console.error('Error deleting module:', error);
        this.$emit('show-message', 'Failed to delete module', 'error');
      }
    },

    async deleteSubthread(subthread, module) {
      if (!confirm('Are you sure you want to delete this interaction?')) {
        return;
      }

      try {
        await this.$axios.delete(`api/careplansubthreads/${subthread.id}/`);
        this.$emit('show-message', 'Interaction deleted successfully', 'success');
        this.loadCarePlan();
      } catch (error) {
        console.error('Error deleting subthread:', error);
        this.$emit('show-message', 'Failed to delete interaction', 'error');
      }
    },

    closeEditModal() {
      this.selectedModule = null;
      this.selectedThreadType = null;
    },

    closeViewModal() {
      this.viewingModule = null;
      this.viewingThreadType = null;
    },

    handleEditFromView(module) {
      this.closeViewModal();
      this.editModule(module);
    },

    onModuleAdded() {
      this.showAddModule = false;
      this.loadCarePlan();
    },
  },
};
</script>

<style scoped>
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
